import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "../lib/api";
import { RiskBadge } from "../components/shared/RiskBadge";
import { EmptyState } from "../components/shared/EmptyState";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ListChecks, ArrowRight } from "lucide-react";
import { capture, EVENTS } from "../analytics/posthog";

const SOL = { DELEGATE: "Delegar", DOCUMENT: "Documentar", TRAIN: "Formar", AUTOMATE: "Automatizar", ELIMINATE: "Eliminar", REDESIGN: "Rediseñar" };

export default function PlanPage() {
  const navigate = useNavigate();
  const [plan, setPlan] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/plan").then(({ data }) => setPlan(data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  }, []);

  const createProject = async (item) => {
    setBusy(item.dependency_id);
    try {
      const { data } = await api.post("/projects", { objective: item.title, dependency_ids: [item.dependency_id] });
      capture(EVENTS.projectCreated, { source: "plan" });
      navigate(`/app/projects/${data.id}`);
    } catch (e) { toast.error(apiErr(e)); setBusy(null); }
  };

  if (loading) return <div className="mono-label text-slate-500">CARGANDO PLAN…</div>;
  if (!plan.length) return <EmptyState icon={ListChecks} title="Tu Independence Plan aparecerá aquí"
    description="Completa el diagnóstico para recibir tus prioridades ordenadas por impacto y riesgo."
    action={<Button onClick={() => navigate("/audit")} className="bg-amber-500 text-black hover:bg-amber-400 rounded-full font-semibold">Comenzar diagnóstico</Button>} />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-head text-2xl sm:text-3xl font-bold text-white">Independence Plan</h1>
        <p className="mt-1 text-slate-400">Prioridades ordenadas por impacto y riesgo. Convierte cualquiera en un proyecto para que Architect lo construya.</p>
      </div>
      <div className="space-y-4">
        {plan.map((item, i) => (
          <div key={item.dependency_id} className="card-arch p-5 sm:p-6" data-testid={`plan-item-${i}`}>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex items-start gap-3 sm:gap-4 min-w-0">
                <span className="font-head text-xl sm:text-2xl font-extrabold text-slate-600 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2"><h3 className="font-head text-base sm:text-lg font-semibold text-white">{item.title}</h3><RiskBadge severity={item.severity} /></div>
                  {item.problem && <p className="mt-2 text-sm text-slate-400">{item.problem}</p>}
                  {item.why && <p className="mt-2 text-sm text-slate-500"><span className="text-slate-400">Por qué importa:</span> {item.why}</p>}
                  {item.expected_impact && <p className="mt-2 text-sm text-emerald-400/90"><span className="text-slate-400">Impacto esperado:</span> {item.expected_impact}</p>}
                  <span className="mt-3 inline-block text-xs font-medium text-amber-400">Solución: {SOL[item.solution] || item.solution || "—"}</span>
                </div>
              </div>
              <Button data-testid={`plan-create-project-${i}`} onClick={() => createProject(item)} disabled={busy === item.dependency_id}
                className="w-full sm:w-auto shrink-0 bg-amber-500 text-black hover:bg-amber-400 rounded-full font-semibold">
                {busy === item.dependency_id ? "…" : <>Resolver con Architect <ArrowRight className="ml-2 h-4 w-4" /></>}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
